import { MapBoxMap } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
// eslint-disable-next-line import/no-extraneous-dependencies
import Mapboxgl from 'mapbox-gl';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import EvStationPropType from '../../prop-types/evStation';
import styles from './EvMapView.module.css';

const cx = classnames.bind(styles);

// Default center: Geographic center of continental US
const US_CENTER = {
  lat: 39.8283,
  lng: -98.5795,
};

// Default zoom for country overview
const DEFAULT_ZOOM = 4;

// Zoom threshold for marker-to-dot conversion
const DOT_ZOOM_THRESHOLD = 10;

// Popup offset from marker (pixels above the marker)
const POPUP_OFFSET_Y = 40;

// Popup dimensions for boundary calculations
const POPUP_WIDTH = 320; // Approximate popup width
const POPUP_HEIGHT = 300; // Approximate popup height
const POPUP_PADDING = 16; // Minimum padding from map edges

/**
 * Check if station is under repair/temporarily unavailable
 */
function isStationUnderRepair(station) {
  return station.statusCode === 'T';
}

/**
 * Check if station accepts Pay with GasBuddy+
 * PWGB+ cards are identified by FuelMan or M in cardsAccepted field
 */
function acceptsPwgbPlus(station) {
  if (!station.cardsAccepted) return false;
  const cards = station.cardsAccepted.toUpperCase();
  return cards.includes('FUELMAN') || cards.includes(',M') || cards.startsWith('M,') || cards === 'M';
}

/**
 * Get marker color class based on station's highest charging level
 * Priority: DC Fast > Level 2 > Level 1
 */
function getMarkerColorClass(station) {
  if (station.evDcFastNum > 0) {
    return 'markerDcFast';
  }
  if (station.evLevel2EvseNum > 0) {
    return 'markerLevel2';
  }
  return 'markerLevel1';
}

/**
 * EvMapView component - MapBox map wrapper for EV charging stations
 *
 * Displays stations as markers with letter labels (A, B, C...) and
 * color-coding by charging level (DC Fast = orange, Level 2 = blue, Level 1 = gray).
 * At low zoom levels (< 10), markers convert to dots.
 */
export default function EvMapView({
  stations,
  selectedStation,
  selectedStationId,
  highlightedStationId,
  listSelectedStationId,
  center,
  zoom,
  accessToken,
  onStationSelect,
  onStationHover,
  onMapMove,
  onMapLoad,
  onZoomChange,
  fitBounds,
  renderPopup,
}) {
  const mapRef = useRef();
  const mapInstanceRef = useRef(null);
  const currentZoomRef = useRef(zoom);
  const [popupPosition, setPopupPosition] = useState(null);

  // Track previous center prop to detect location changes
  const prevCenterRef = useRef(center);

  // Track if map has loaded
  const mapLoadedRef = useRef(false);

  const handleMapLoad = useCallback((map) => {
    if (map) {
      // Store map instance for popup positioning
      mapInstanceRef.current = map;
      mapLoadedRef.current = true;

      // If we have a center prop that's different from US_CENTER, fly to it immediately
      // This handles the case where geolocation completed before map loaded
      if (center && (center.lat !== US_CENTER.lat || center.lng !== US_CENTER.lng)) {
        map.flyTo({
          center: [center.lng, center.lat],
          zoom: zoom || 11,
          duration: 0, // Instant jump, no animation
        });
      }

      if (onMapLoad) {
        const bounds = map.getBounds();
        onMapLoad({
          bounds: {
            northEast: { lat: bounds.getNorthEast().lat, lng: bounds.getNorthEast().lng },
            southWest: { lat: bounds.getSouthWest().lat, lng: bounds.getSouthWest().lng },
          },
        });
      }
    }
  }, [center, zoom, onMapLoad]);

  // Calculate popup position when selected station changes or map moves
  // Ensures popup stays within map bounds by adjusting position when near edges
  const updatePopupPosition = useCallback(() => {
    if (selectedStation && mapInstanceRef.current) {
      try {
        const map = mapInstanceRef.current;
        const container = map.getContainer();
        const { clientWidth: containerWidth } = container;
        const point = map.project([selectedStation.longitude, selectedStation.latitude]);

        // Calculate ideal position (centered above marker)
        let { x } = point;
        let y = point.y - POPUP_OFFSET_Y;

        // Calculate popup bounds at ideal position
        const halfWidth = POPUP_WIDTH / 2;
        const leftEdge = x - halfWidth;
        const rightEdge = x + halfWidth;
        const topEdge = y - POPUP_HEIGHT;

        // Horizontal boundary adjustment
        if (leftEdge < POPUP_PADDING) {
          // Too close to left edge - shift popup right
          x = halfWidth + POPUP_PADDING;
        } else if (rightEdge > containerWidth - POPUP_PADDING) {
          // Too close to right edge - shift popup left
          x = containerWidth - halfWidth - POPUP_PADDING;
        }

        // Vertical boundary adjustment
        if (topEdge < POPUP_PADDING) {
          // Too close to top edge - position popup below the marker instead
          // Use smaller offset since popup will grow downward from this point
          y = point.y + POPUP_OFFSET_Y;
        }

        setPopupPosition({
          x,
          y,
          // Flag to indicate if popup is positioned below marker (for arrow direction)
          isBelow: topEdge < POPUP_PADDING,
        });
      } catch (e) {
        // Map not ready yet, will retry on next update
        setPopupPosition(null);
      }
    } else {
      setPopupPosition(null);
    }
  }, [selectedStation]);

  // onPanEnd receives the center coordinates {lat, lng} from MapBoxMap
  const handlePanEnd = useCallback((newCenter) => {
    if (onMapMove && mapInstanceRef.current) {
      const bounds = mapInstanceRef.current.getBounds();
      onMapMove({
        center: { lat: newCenter.lat, lng: newCenter.lng },
        bounds: {
          northEast: { lat: bounds.getNorthEast().lat, lng: bounds.getNorthEast().lng },
          southWest: { lat: bounds.getSouthWest().lat, lng: bounds.getSouthWest().lng },
        },
      });
    }
    // Update popup position after pan
    updatePopupPosition();
  }, [onMapMove, updatePopupPosition]);

  // onZoomChange receives the zoom level number from MapBoxMap
  const handleZoomChange = useCallback((newZoom) => {
    currentZoomRef.current = newZoom;
    if (onZoomChange) {
      onZoomChange(newZoom);
    }
    // Update popup position after zoom
    updatePopupPosition();
  }, [onZoomChange, updatePopupPosition]);

  // Update popup position when selected station changes
  useEffect(() => {
    updatePopupPosition();
  }, [updatePopupPosition]);

  // Update popup position when selected station changes
  // Don't fly to the station - just show popup at current position
  useEffect(() => {
    if (selectedStation && mapInstanceRef.current) {
      updatePopupPosition();
    }
  }, [selectedStation, updatePopupPosition]);

  // Handle center prop changes by flying to new location directly
  // This is triggered when user searches for a new location
  useEffect(() => {
    if (!center || !mapLoadedRef.current || !mapInstanceRef.current) {
      return;
    }

    const prevCenter = prevCenterRef.current;
    const centerChanged = !prevCenter || prevCenter.lat !== center.lat || prevCenter.lng !== center.lng;

    if (centerChanged) {
      prevCenterRef.current = center;
      // Fly to new location - this bypasses MapBoxMap's setCenter effect
      mapInstanceRef.current.flyTo({
        center: [center.lng, center.lat],
        zoom: zoom || 11,
        duration: 500,
      });
    }
  }, [center, zoom]);

  // Determine if we should show dots or full markers based on zoom
  const showDots = zoom < DOT_ZOOM_THRESHOLD;

  // Store markers ref for cleanup
  const markersRef = useRef([]);

  // Create custom markers with click handlers (MapBoxMap.Marker doesn't support onClick)
  useEffect(() => {
    if (!mapInstanceRef.current || !stations || stations.length === 0) {
      return undefined;
    }

    const map = mapInstanceRef.current;

    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Create new markers
    stations.forEach((station) => {
      // Skip stations without valid coordinates
      if (!station.latitude || !station.longitude) {
        return;
      }

      const isSelected = selectedStationId === station.id;
      const isHighlighted = highlightedStationId === station.id;
      const isListSelected = listSelectedStationId === station.id;
      const colorClass = getMarkerColorClass(station);
      const underRepair = isStationUnderRepair(station);
      const hasPwgb = acceptsPwgbPlus(station);

      // Create marker element
      const el = document.createElement('div');
      el.className = cx(
        'marker',
        colorClass,
        {
          markerSelected: isSelected,
          markerHighlighted: isHighlighted,
          markerListSelected: isListSelected,
          markerDot: showDots,
          markerUnderRepair: underRepair,
          markerPwgb: hasPwgb,
        },
      );

      // Add click handler
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onStationSelect) {
          onStationSelect(station);
        }
      });

      // Add hover handlers
      el.addEventListener('mouseenter', () => {
        if (onStationHover) {
          onStationHover(station);
        }
      });

      el.addEventListener('mouseleave', () => {
        if (onStationHover) {
          onStationHover(null);
        }
      });

      // Create MapBox marker
      const marker = new Mapboxgl.Marker(el)
        .setLngLat([station.longitude, station.latitude])
        .addTo(map);

      markersRef.current.push(marker);
    });

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    };
  }, [
    stations,
    selectedStationId,
    highlightedStationId,
    listSelectedStationId,
    showDots,
    onStationSelect,
    onStationHover,
  ]);


  // Track if fitBounds has been applied for the current fitBounds=true cycle
  const fitBoundsAppliedRef = useRef(false);

  // Reset the applied flag when fitBounds transitions to false
  useEffect(() => {
    if (!fitBounds) {
      fitBoundsAppliedRef.current = false;
    }
  }, [fitBounds]);

  // Handle fitBounds - apply when fitBounds is true AND we have stations AND haven't applied yet
  // This ensures we wait for stations to load before fitting bounds
  useEffect(() => {
    if (fitBounds && stations.length > 0 && mapInstanceRef.current && !fitBoundsAppliedRef.current) {
      fitBoundsAppliedRef.current = true;
      const map = mapInstanceRef.current;
      const bounds = new Mapboxgl.LngLatBounds();

      stations.forEach((station) => {
        if (station.latitude && station.longitude) {
          bounds.extend([station.longitude, station.latitude]);
        }
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 50, maxZoom: 14, duration: 0 });
      }
    }
  }, [fitBounds, stations]);

  return (
    <div className={cx('mapContainer')} ref={mapRef}>
      <MapBoxMap
        accessToken={accessToken}
        center={center || US_CENTER}
        height="100%"
        onLoad={handleMapLoad}
        onPanEnd={handlePanEnd}
        onZoomChange={handleZoomChange}
        zoom={zoom || DEFAULT_ZOOM}
      />

      {/* Popup rendered above or below the selected marker based on position */}
      {selectedStation && popupPosition && renderPopup && (
        <div
          className={cx('markerPopup', { markerPopupBelow: popupPosition.isBelow })}
          style={{
            left: popupPosition.x,
            top: popupPosition.y,
          }}
        >
          {renderPopup(selectedStation, { isBelow: popupPosition.isBelow })}
        </div>
      )}
    </div>
  );
}

EvMapView.propTypes = {
  stations: PropTypes.arrayOf(EvStationPropType),
  selectedStation: EvStationPropType,
  selectedStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  highlightedStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  listSelectedStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  zoom: PropTypes.number,
  accessToken: PropTypes.string.isRequired,
  onStationSelect: PropTypes.func,
  onStationHover: PropTypes.func,
  onMapMove: PropTypes.func,
  onMapLoad: PropTypes.func,
  onZoomChange: PropTypes.func,
  renderPopup: PropTypes.func,
  fitBounds: PropTypes.bool,
};

EvMapView.defaultProps = {
  stations: [],
  selectedStation: null,
  selectedStationId: null,
  highlightedStationId: null,
  listSelectedStationId: null,
  center: US_CENTER,
  zoom: DEFAULT_ZOOM,
  onStationSelect: undefined,
  onStationHover: undefined,
  onMapMove: undefined,
  onMapLoad: undefined,
  onZoomChange: undefined,
  renderPopup: undefined,
  fitBounds: false,
};

// Export utility functions for testing
export { getMarkerColorClass, isStationUnderRepair };
